import * as React from 'react';
import { FormControl, FormHelperText, Grid, Input, InputLabel, Typography } from '@mui/material';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import { useTranslation } from 'react-i18next';
import css from './View.module.css';
import { Button } from 'app/components/Button';
import { RootState } from 'app/store/types';
import { connect } from 'react-redux';
import { InjectedFormProps, getFormValues } from 'redux-form';
import { AuthorizeSsnFullPull } from 'app/components/FormAgreements/AuthorizeSsnFullPull';
import { formNameSelector } from 'app/reducers/app-config';
import { ButtonDelay } from 'app/components/ButtonDelay';
import { toFormattedSsn } from 'app/util/formatters';
import { toNormalizedSsn } from 'app/util/normalizers';
import { FieldNames } from 'app/models/fields/names';
import { isYes } from 'app/models/fields/conditionals';

type ConnectProps = ReturnType<typeof mapStateToProps>;
type OwnProps     = { getPreviousSection?: () => void };
type Props        = ConnectProps & InjectedFormProps & OwnProps;

const mapStateToProps = (state: RootState) => {
  const formName   = formNameSelector(state);
  const formValues = formName ? getFormValues(formName)(state) as any : {};
  const hasCoApplicant = isYes(formValues?.[FieldNames.coBorrowerYN]);
  return { formName, hasCoApplicant };
};

/**
 * SSN collection step for the Commercial Lease Ag Facilities form.
 * Always collects the applicant SSN. Conditionally collects the co-applicant
 * SSN when the co-applicant radio is answered Yes.
 * SSNs are kept out of Redux state via refs.
 */
export let applicantSsnRef: HTMLInputElement | undefined     = undefined;
export let coApplicantSsnRef: HTMLInputElement | undefined   = undefined;

const isValidSsn = (value: string): boolean => /\d{9}/.test(value);

const CollectAgFacilitiesSsnComponent = (props: Props) => {
  const { t } = useTranslation();
  const { submitting, hasCoApplicant } = props;

  const [applicantSsn, setApplicantSsn]     = React.useState('');
  const [coApplicantSsn, setCoApplicantSsn] = React.useState('');

  const onApplicantChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    applicantSsnRef.value = toFormattedSsn(event.target.value);
    setApplicantSsn(toNormalizedSsn(applicantSsnRef.value));
  };

  const onCoApplicantChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    coApplicantSsnRef.value = toFormattedSsn(event.target.value);
    setCoApplicantSsn(toNormalizedSsn(coApplicantSsnRef.value));
  };

  const isApplicantSsnValid   = isValidSsn(applicantSsn);
  const isCoApplicantSsnValid = !hasCoApplicant || isValidSsn(coApplicantSsn);
  const canSubmit             = isApplicantSsnValid && isCoApplicantSsnValid && !submitting;

  return (
    <div style={{ padding: '16px 26px' }}>
      <Grid container>
        <Grid item xs={12}>
          <Typography variant='h6'>Social Security Number</Typography>
        </Grid>
      </Grid>

      {/* Applicant SSN */}
      <Grid container>
        <Grid item xs={12} classes={{ item: 'wrapper' }}>
          <FormControl error={applicantSsn.length > 0 && !isApplicantSsnValid} fullWidth>
            <InputLabel color='primary' htmlFor='applicantSsn'>
              {t('commercialleaseagfacilities.ssn.applicant.label', { defaultValue: 'Applicant SSN' })}
            </InputLabel>
            <Input
              id       = 'applicantSsn'
              type     = 'text'
              onChange = {onApplicantChange}
              inputRef = {ref => applicantSsnRef = ref}
            />
            {applicantSsn.length > 0 && !isApplicantSsnValid && (
              <FormHelperText>Invalid social security number</FormHelperText>
            )}
          </FormControl>
        </Grid>
      </Grid>

      {/* Co-Applicant SSN — shown only when co-applicant name was entered */}
      {hasCoApplicant && (
        <Grid container style={{ marginTop: 16 }}>
          <Grid item xs={12} classes={{ item: 'wrapper' }}>
            <FormControl error={coApplicantSsn.length > 0 && !isValidSsn(coApplicantSsn)} fullWidth>
              <InputLabel color='primary' htmlFor='coApplicantSsn'>
                {t('commercialleaseagfacilities.ssn.coapplicant.label', { defaultValue: 'Co-Applicant SSN' })}
              </InputLabel>
              <Input
                id       = 'coApplicantSsn'
                type     = 'text'
                onChange = {onCoApplicantChange}
                inputRef = {ref => coApplicantSsnRef = ref}
              />
              {coApplicantSsn.length > 0 && !isValidSsn(coApplicantSsn) && (
                <FormHelperText>Invalid social security number</FormHelperText>
              )}
            </FormControl>
          </Grid>
        </Grid>
      )}

      <AuthorizeSsnFullPull />
      <div className={css.navWrapper}>
        <div className={css.pageNav}>
          <Grid container className={css.navContainer}>
            <div className={`${css.navButton} ${css.saveLink}`}></div>
            <div className={css.navButton}>
              <Button
                disableRipple
                size      = 'small'
                className = {css.backButton}
                onClick   = {props.getPreviousSection}
              >
                <ChevronLeft /> {t('global:button.back', { defaultValue: 'Back' })}
              </Button>
            </div>
            <div className={`${css.navButton}`} style={{ marginRight: 16 }}>
              <ButtonDelay
                loading     = {submitting}
                buttonProps = {{
                  color    : 'primary',
                  disabled : !canSubmit,
                  variant  : 'contained',
                  type     : 'submit',
                }}
              >
                {t('global:button.submit', { defaultValue: 'Submit' })}
              </ButtonDelay>
            </div>
          </Grid>
        </div>
      </div>
    </div>
  );
};

export const CollectAgFacilitiesSsnStep = connect(mapStateToProps)(CollectAgFacilitiesSsnComponent);
